export const SYBIL_CLUSTER_STATUSES = ['review', 'blocked', 'cleared'] as const
export type SybilClusterStatus = (typeof SYBIL_CLUSTER_STATUSES)[number]

export const SYBIL_CLUSTER_STATUS_LABELS: Record<SybilClusterStatus, string> = {
    review: 'Review',
    blocked: 'Blocked',
    cleared: 'Cleared',
}

// Correlator slugs as blockpot-service `sybil.CorrelatorKind` sends them. Open union so a new
// correlator added on the service side doesn't crash the UI.
export type SybilCorrelator = 'fingerprint' | 'ip' | 'origin_vasp' | 'behavioral' | (string & {})

export const SYBIL_CORRELATOR_LABELS: Record<string, string> = {
    fingerprint: 'Device fingerprint',
    ip: 'Residential IP',
    origin_vasp: 'Funding origin (CEX)',
    behavioral: 'Behavioral pattern',
}

// MLRO disposition on a wallet_correlation_status row (`sybil.Outcome`).
export type SybilWalletOutcome = 'cleared' | 'flagged' | 'blocked' | (string & {})

// --- Wire shapes ---------------------------------------------------------------------------
// blockpot-service `internal/httpapi/sybil_handler.go` serialises these in snake_case. The
// service is the source of truth (BLO-937), so `src/lib/sybil-wire.ts` maps them to the
// console shapes below. Only `api.ts` and fixtures should reference the wire types.

// `clusterListItem`. Times are RFC 3339; wallets are checksummed hex.
export interface SybilClusterWire {
    id: string
    computed_at: string
    correlator: SybilCorrelator
    size: number
    wallets: string[]
    is_anonymizing: boolean
}

// `clusterListResponse`. `next_cursor` is omitempty.
export interface SybilClusterListWire {
    clusters: SybilClusterWire[]
    next_cursor?: string
}

// MLRO clear reasons (`sybil.ClearReason`, founder decision 2026-10-07 on BLO-941), in the
// service's display order. The labels are the approved console copy.
export const SYBIL_CLEAR_REASONS = [
    'distinct_household',
    'public_network',
    'shared_exchange',
    'false_positive',
] as const
export type SybilClearReason = (typeof SYBIL_CLEAR_REASONS)[number]

export const SYBIL_CLEAR_REASON_LABELS: Record<SybilClearReason, string> = {
    distinct_household: 'Shared household or network, players verified as distinct people',
    public_network: 'A café, campus or mobile carrier',
    shared_exchange: 'The funding source is a large exchange used by many unrelated players',
    false_positive: 'A correlator false positive, explained in the note',
}

// `sybil.ClearNoteMinChars`: counted in characters after trimming.
export const SYBIL_CLEAR_NOTE_MIN = 20

// `sybilWalletEvidenceWire`: coarse match evidence only (BLO-941). The ip fields are present only
// for the ip correlator, every one of them nullable: `network` is null once the matching signal
// has aged out, and `asn` and the three flags stay null until a feed fills them (BLO-835). A null
// flag means "not checked", never "no". `vasp` is present only for origin_vasp.
export interface SybilWalletEvidenceWire {
    correlator: SybilCorrelator
    // First 8 hex characters of the shared value's hash.
    match_ref: string
    // A /24 (IPv4) or /48 (IPv6) network, never a full IP address.
    network?: string | null
    asn?: string | null
    is_vpn?: boolean | null
    is_tor?: boolean | null
    is_datacenter?: boolean | null
    vasp?: string
}

// `sybilWalletPlayer`: the player behind a wallet. `tier` is null when the chain read failed.
export interface SybilWalletPlayerWire {
    player_id: string
    tier: string | null
    status: string
}

// `walletStatusItem`. A wallet with no status row sends zeros and omits the optional fields.
// `player` and `case_id` are always present and null when absent.
export interface SybilWalletStatusWire {
    wallet: string
    highest_cluster_size: number
    correlator_breach: number
    outcome?: SybilWalletOutcome
    reviewed_at?: string
    reviewed_by_address?: string
    clear_reason?: SybilClearReason
    clear_note?: string
    player: SybilWalletPlayerWire | null
    case_id: string | null
    evidence: SybilWalletEvidenceWire
}

// `clusterDetailResponse`: the list item with per-wallet statuses.
export interface SybilClusterDetailWire extends SybilClusterWire {
    statuses: SybilWalletStatusWire[]
}

// `sybilClearResponse`.
export interface SybilClearWalletWire {
    wallet: string
    outcome: SybilWalletOutcome
}

// `sybilClusterClearResponse`.
export interface SybilClusterClearWire {
    cluster_id: string
    outcome: SybilWalletOutcome
    reason: SybilClearReason
    wallets: string[]
}

// --- Console shapes ------------------------------------------------------------------------

export interface SybilClusterSummary {
    clusterId: string
    // When the correlator last computed this cluster.
    computedAt: string
    correlator: SybilCorrelator
    size: number
    walletAddresses: string[]
    // The shared value is a VPN, Tor or datacenter IP: reviewed at the lower threshold and
    // counted as one wallet toward the block threshold.
    isAnonymizing: boolean
}

export interface SybilClusterListResponse {
    clusters: SybilClusterSummary[]
    nextCursor: string | null
}

export interface SybilClusterListFilters {
    status?: SybilClusterStatus | ''
}

export interface SybilWalletEvidence {
    correlator: SybilCorrelator
    matchRef: string
    // Null when the service sent none, or anything finer than a /24 or /48 (`coarseNetwork`).
    network: string | null
    asn: string | null
    // Null means not checked, never "no".
    isVpn: boolean | null
    isTor: boolean | null
    isDatacenter: boolean | null
    vasp: string | null
}

export interface SybilWalletPlayer {
    playerId: string
    tier: string | null
    status: string
}

export interface SybilWalletStatus {
    walletAddress: string
    // Largest cluster across every correlator, the figure the pre-tx gate checks.
    highestClusterSize: number
    // Number of correlators whose cluster put this wallet over a threshold.
    correlatorBreach: number
    outcome: SybilWalletOutcome | null
    // Derived as the service's `Status.ReviewState()`; null below the review threshold.
    reviewState: SybilClusterStatus | null
    reviewedAt: string | null
    reviewedByAddress: string | null
    clearReason: SybilClearReason | null
    clearNote: string | null
    player: SybilWalletPlayer | null
    // The case the correlator auto-opened for this wallet, if any.
    caseId: string | null
    evidence: SybilWalletEvidence
}

export interface SybilClusterDetail extends SybilClusterSummary {
    // Derived from the wallets, as the service's list `status` filter does; null when no wallet
    // has reached review.
    status: SybilClusterStatus | null
    wallets: SybilWalletStatus[]
}

// Body of both `POST /v1/players/{addr}/sybil-clear` and `POST /v1/sybil/clusters/{id}/clear`.
export interface SybilClearRequest {
    reason: SybilClearReason
    note: string
}

export interface SybilClearWalletResponse {
    walletAddress: string
    outcome: SybilWalletOutcome
}

export interface SybilClusterClearResponse {
    clusterId: string
    outcome: SybilWalletOutcome
    reason: SybilClearReason
    walletAddresses: string[]
}
